import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLSelectElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /**
   * Handles the quantity set event (for select dropdown).
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const min = parseInt(quantityInput.dataset.min || '1');
    const max = quantityInput.dataset.max ? parseInt(quantityInput.dataset.max) : null;
    const maxOrderQuantity = quantityInput.dataset.maxOrderQuantity ? parseInt(quantityInput.dataset.maxOrderQuantity) : null;
    const newValueInt = parseInt(quantityInput.value);

    // If value is below min, find and select the first option >= min
    if (newValueInt < min) {
      const options = Array.from(quantityInput.options);
      const validOption = options.find(option => parseInt(option.value) >= min);
      if (validOption) {
        quantityInput.value = validOption.value;
        return;
      }
    }
    
    // If value is above max, find and select the last option <= max
    if (max && newValueInt > max) {
      const options = Array.from(quantityInput.options);
      const validOptions = options.filter(option => parseInt(option.value) <= max);
      if (validOptions.length > 0) {
        quantityInput.value = validOptions[validOptions.length - 1].value;
        return;
      }
    }

    // Check max_order_quantity limit (for product page, cart validation happens separately)
    if (maxOrderQuantity && maxOrderQuantity > 0 && !quantityInput.dataset.cartLine) {
      if (newValueInt > maxOrderQuantity) {
        const options = Array.from(quantityInput.options);
        const validOptions = options.filter(option => {
          const optionValue = parseInt(option.value);
          return optionValue <= maxOrderQuantity && optionValue >= min;
        });
        if (validOptions.length > 0) {
          quantityInput.value = validOptions[validOptions.length - 1].value;
        }
      }
    }
  };

  /**
   * Gets the quantity input (select element).
   * @returns {HTMLSelectElement} The quantity select.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <select ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
